/**
 * Which of your apps each settings page actually changes.
 *
 * WHY LABEL RATHER THAN HIDE. Settings were briefly scoped to the shell you were
 * in, so Publish's Connections vanished inside Brainstorms. That was wrong for a
 * reason worth writing down: **there is one settings store**. The AI model you
 * pick in Brainstorms is the same row Publish reads. Hiding a tab implies a
 * separation the data does not have, and it creates the worse confusion — "where
 * did that tab go?" is harder to recover from than "why is this here?", because
 * the second question answers itself the moment you read the label.
 *
 * ENTITLEMENT STILL GATES. This is only about the shell you are standing in. A
 * customer who does not own Publish never sees Connections at all; one who owns
 * both sees it everywhere, labelled Publish.
 *
 * ADDING A PRODUCT IS ONE LINE HERE. That is the point of the map: a new app
 * lands in every label at once instead of being missed in whichever screens
 * nobody remembered.
 *
 * Pure and dependency-free so CRA's jest can test it.
 */
import { PRODUCT_INFO, PRODUCT_ORDER } from '../shell/products';
import type { Product } from '../types';

/** Every product, for settings that genuinely affect all of them. */
export const ALL_PRODUCTS: Product[] = PRODUCT_ORDER;

/**
 * Settings tab -> the products it changes.
 *
 * The test for "all products" is SCOPE OF EFFECT, not who tends to use it:
 *   AI       every product bills LLM stages — StageEnum has NEWS_BEAT_AGENT
 *            beside GENERATE_ARTICLE
 *   Network  boardroom lookups, beats fetch articles, publish crawls SERPs
 *   Brand    the reader renders all three artifact kinds in the sender's brand
 * and the two that are genuinely one product's:
 *   Connections      GitHub deploys, Google Ads feeds keywords, Amazon monetises pages
 *   Page Generation  page specs and builder research
 */
/**
 * Connections sub-view -> the products it changes.
 *
 * SCOPING STOPS AT THE TAB AND THAT IS NOT ENOUGH HERE. Connections is a hub of
 * unrelated credentials, and tagging the whole thing `PUBLISH` was wrong in a way
 * that would have cost a customer something real: **News API serves Beats too**
 * (`jcseo/tasks/news_beats.py`) — so a Beats-only account would have had the
 * entire tab hidden by the entitlement gate and lost access to its own GNews key.
 *
 * News API is ALSO Publish: `jcseo/trends/briefs.py:226` calls `gnews.search()`
 * to enrich a trend brief. One credential, two products, and neither of them
 * optional.
 */
/** Every product any of these scopes touches, in catalog order.
 *
 *  DERIVED rather than hand-written wherever one scope contains others, so a
 *  container cannot claim a narrower scope than the things inside it — which is
 *  exactly how Connections came to claim it was Publish-only. */
function unionOf(scopes: Record<string, Product[]>): Product[] {
  const seen = new Set<Product>();
  Object.values(scopes).forEach((products) => products.forEach((p) => seen.add(p)));
  return ALL_PRODUCTS.filter((p) => seen.has(p));
}

/** The credentials themselves. `usage` is derived from these below — it is a
 *  report ABOUT them, so it can never cover a product none of them touch. */
const CONNECTION_CREDENTIALS: Record<string, Product[]> = {
  github: ['PUBLISH'], // deploys a site
  'google-ads': ['PUBLISH'], // keyword volumes
  'image-storage': ['PUBLISH'], // images inside pages
  amazon: ['PUBLISH'], // affiliate links inside pages
  // GNews. `jcseo/tasks/news_beats.py` fetches articles with it and
  // `jcseo/trends/briefs.py:226` enriches a trend brief with it.
  news: ['BEATS', 'PUBLISH'],
};

export const CONNECTION_SUB_APPLIES_TO: Record<string, Product[]> = {
  ...CONNECTION_CREDENTIALS,
  // The external-API spend panel reports on the providers above and nothing
  // else. Tagging it "all products" was wrong in a quiet way: it dragged
  // Brainstorm into the hub's derived scope, and Brainstorm bills no external
  // API on this table at all.
  usage: unionOf(CONNECTION_CREDENTIALS),
  // The mailer's email spy: Recon signs up to competitors' forms with its addresses.
  'email-spy': ['BOARDROOM'],
};

export const SETTINGS_APPLIES_TO: Record<string, Product[]> = {
  ai: ALL_PRODUCTS,
  network: ALL_PRODUCTS,
  brand: ALL_PRODUCTS,
  connections: unionOf(CONNECTION_SUB_APPLIES_TO),
  'page-generation': ['PUBLISH'],
  // The ★ in the keyword lists, and recon's "Where to win".
  keywords: ['BOARDROOM', 'PUBLISH'],
  // Administration, not product configuration. No label — saying "applies to
  // everything" about user approvals would be noise pretending to be
  // information.
  users: [],
};

/** The products this tab affects that the user actually has. */
export function appliesTo(tabKey: string, held: Product[]): Product[] {
  const scope = SETTINGS_APPLIES_TO[tabKey] ?? [];
  return scope.filter((p) => held.includes(p));
}

/** The products a Connections sub-view affects that the user actually has. */
export function subAppliesTo(subKey: string, held: Product[]): Product[] {
  const scope = CONNECTION_SUB_APPLIES_TO[subKey] ?? [];
  return scope.filter((p) => held.includes(p));
}

/** Whether a sub-view is worth showing at all — it configures something none of
 *  your products use otherwise. */
export function subIsRelevant(subKey: string, held: Product[]): boolean {
  const scope = CONNECTION_SUB_APPLIES_TO[subKey];
  // An unmapped sub is shown rather than hidden: a missing entry is an omission,
  // and hiding a credential because nobody wrote a line in a map is the worse
  // failure of the two.
  if (!scope) return true;
  return scope.some((p) => held.includes(p));
}

/** The label for one Connections sub-view, or null when it would say nothing. */
export function subAppliesToLabel(subKey: string, held: Product[]): string | null {
  if (held.length < 2) return null;
  const products = subAppliesTo(subKey, held);
  if (products.length === 0 || products.length === held.length) return null;
  const names = products.map((p) => PRODUCT_INFO[p]?.short ?? p);
  if (names.length === 1) return `Applies to ${names[0]}`;
  return `Applies to ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The sentence to show under a settings page, or null for no label.
 *
 * NULL WHEN IT WOULD SAY NOTHING. Two cases: a tab with no product scope
 * (Users), and a customer who owns exactly one product — telling somebody who
 * only has News Beats that a setting "applies to News Beats" is a label that
 * cannot be false, and a label that cannot be false is furniture.
 */
export function appliesToLabel(tabKey: string, held: Product[]): string | null {
  if (held.length < 2) return null;

  const products = appliesTo(tabKey, held);
  if (products.length === 0) return null;

  const names = products.map((p) => PRODUCT_INFO[p]?.short ?? p);
  if (names.length === held.length) return 'Applies to all your apps';
  if (names.length === 1) return `Applies to ${names[0]}`;
  return `Applies to ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
